



























import { computed, defineComponent, onBeforeUnmount, onMounted, PropType, ref, watch } from 'vue';
import { PostModel } from '../../../shared/models/post.model';
import MediaThumbnail from '../../../shared/components/MediaThumbnail.vue';
import { useUserProfileStore } from '../../../shared/stores';
import { ScanningStatusEnum } from '../../../shared/enums/scanningStatus.enum';
import { mediaTypes } from '../../../shared/enums/mediaTypes';
import { AttachmentModel } from '../../../shared/models/attachment.model';
import Icon from '../../../shared/components/Icon.vue';
import { iconClassEnum } from '../../../shared/enums/iconClassEnum';

const THUMB_WIDTH_PX = 100;
const GAP_PX = 8;

export default defineComponent({
  name: 'SearchResultPostAttachments',
  components: { Icon, MediaThumbnail },
  props: {
    post: { type: Object as PropType<PostModel>, required: true },
  },
  setup(props) {
    const measureHostRef = ref<HTMLElement | null>(null);
    const measuredWidth = ref(0);
    const hasMeasured = ref(false);
    const attachments = ref<AttachmentModel[]>([]);

    const userProfile = computed(() => useUserProfileStore()?.institutionProfile);

    const filteredAttachments = computed(() => {
      const list: AttachmentModel[] = [];
      for (const attachment of props.post.attachments) {
        const isCreator = attachment.creator?.profileId === userProfile.value?.profileId;
        const isBlocked = attachment.media?.file?.scanningStatus === ScanningStatusEnum.BLOCKED;
        if (!isBlocked || isCreator) {
          list.push(attachment);
        }
      }
      return list;
    });

    watch(
      filteredAttachments,
      list => {
        attachments.value = [...list];
      },
      { immediate: true }
    );

    const maxSlots = computed(() => {
      if (!hasMeasured.value) {
        return Math.max(1, attachments.value.length);
      }
      return Math.max(1, Math.floor((measuredWidth.value + GAP_PX) / (THUMB_WIDTH_PX + GAP_PX)));
    });

    const visibleCount = computed(() => {
      const total = attachments.value.length;
      const slots = maxSlots.value;
      if (total === 0) {
        return 0;
      }
      if (total <= slots) {
        return total;
      }
      return slots - 1;
    });

    const visibleAttachments = computed(() => attachments.value.slice(0, visibleCount.value));

    const hiddenAttachmentCount = computed(() => attachments.value.length - visibleCount.value);

    let resizeObserver: ResizeObserver | null = null;

    const attachResizeObserver = () => {
      const el = measureHostRef.value;
      if (!el || typeof ResizeObserver === 'undefined') {
        return;
      }
      resizeObserver?.disconnect();
      const update = (width: number) => {
        measuredWidth.value = width;
        hasMeasured.value = true;
      };
      resizeObserver = new ResizeObserver(entries => {
        const entry = entries[0];
        const w = entry?.contentRect?.width ?? el.clientWidth;
        update(w);
      });
      resizeObserver.observe(el);
      update(el.clientWidth);
    };

    onMounted(() => {
      attachResizeObserver();
    });

    onBeforeUnmount(() => {
      resizeObserver?.disconnect();
      resizeObserver = null;
    });

    const attachmentKey = (attachment: AttachmentModel) => String(attachment.id);

    return {
      measureHostRef,
      attachments,
      visibleAttachments,
      hiddenAttachmentCount,
      attachmentKey,
      mediaTypes,
      iconClassEnum,
    };
  },
});
